import React, { useEffect, useState, useCallback } from 'react';
import { Container, Card, Table, Badge, Form, Button, Modal, Row, Col } from 'react-bootstrap';
import { ShoppingBagIcon, MagnifyingGlassIcon, PrinterIcon, ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import { companyAPI } from '../../services/api';

const ORDER_STATUSES = ['pending','confirmed','baking','ready','delivered','cancelled'];
const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  pending:   { bg: '#fef3c7', color: '#92400e' },
  confirmed: { bg: '#dbeafe', color: '#1e40af' },
  baking:    { bg: '#fce7f3', color: '#9d174d' },
  ready:     { bg: '#dcfce7', color: '#166534' },
  delivered: { bg: '#d1fae5', color: '#065f46' },
  cancelled: { bg: '#fee2e2', color: '#991b1b' },
};

// ── Print helpers ─────────────────────────────────────────────────────────────

function buildOrderHtml(o: any, storeName: string) {
  const items = (o.items || []).map((i: any) =>
    `<tr>
      <td style="padding:7px 0;border-bottom:1px solid #f1f5f9">${i.quantity}× ${i.product_name}</td>
      <td style="padding:7px 0;border-bottom:1px solid #f1f5f9;text-align:right">$${(parseFloat(i.product_price || 0) * i.quantity).toFixed(2)}</td>
    </tr>`
  ).join('');
  const sub  = parseFloat(o.subtotal || o.total_amount || 0);
  const tip  = parseFloat(o.tip_amount || 0);
  const disc = parseFloat(o.discount_amount || 0);
  const tax  = parseFloat(o.tax_amount || 0);
  const total = parseFloat(o.total_amount || 0);
  const extras = [
    disc > 0  ? `<tr><td style="color:#059669">Discount</td><td style="text-align:right;color:#059669">−$${disc.toFixed(2)}</td></tr>` : '',
    tax  > 0  ? `<tr><td>Tax</td><td style="text-align:right">$${tax.toFixed(2)}</td></tr>` : '',
    tip  > 0  ? `<tr><td>Tip</td><td style="text-align:right">$${tip.toFixed(2)}</td></tr>` : '',
  ].join('');
  return `
    <div style="border-bottom:2px solid #e2e8f0;padding-bottom:20px;margin-bottom:20px">
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div>
          <div style="font-size:1rem;font-weight:800;color:#059669">${o.order_number}</div>
          <div style="font-size:0.82rem;color:#64748b;margin-top:2px">${new Date(o.created_at).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}</div>
        </div>
        <div style="font-size:0.78rem;text-align:right;color:#374151">
          <div><strong>${o.member_name || o.guest_name || 'Guest'}</strong></div>
          ${o.guest_email ? `<div>${o.guest_email}</div>` : ''}
          ${o.guest_phone ? `<div>${o.guest_phone}</div>` : ''}
          ${o.delivery_address ? `<div>${o.delivery_address}</div>` : ''}
        </div>
      </div>
      <!-- Bake date + fulfillment banner -->
      <div style="display:flex;gap:12px;margin-top:14px;margin-bottom:4px">
        <div style="flex:1;background:#f0fdf4;border:2px solid #86efac;border-radius:10px;padding:12px 16px;text-align:center">
          <div style="font-size:0.7rem;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:4px">Bake Date</div>
          <div style="font-size:1.5rem;font-weight:900;color:#15803d">${o.bake_date ? new Date(o.bake_date + 'T12:00').toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'}) : '—'}</div>
        </div>
        <div style="flex:1;background:${o.delivery_address ? '#eff6ff' : '#f0fdf4'};border:2px solid ${o.delivery_address ? '#93c5fd' : '#86efac'};border-radius:10px;padding:12px 16px;text-align:center">
          <div style="font-size:0.7rem;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:4px">Fulfillment</div>
          <div style="font-size:1.5rem;font-weight:900;color:${o.delivery_address ? '#1d4ed8' : '#15803d'}">${o.delivery_address ? '🚚 Delivery' : '🛍 Pickup'}</div>
        </div>
      </div>
      <table style="width:100%;border-collapse:collapse;margin-top:12px;font-size:0.85rem">
        ${items}
        ${extras}
        <tr style="font-weight:800;font-size:0.95rem">
          <td style="padding-top:8px">Total</td>
          <td style="text-align:right;padding-top:8px">$${total.toFixed(2)}</td>
        </tr>
      </table>
      ${o.notes ? `<p style="margin-top:10px;font-size:0.8rem;color:#64748b"><strong>Notes:</strong> ${o.notes}</p>` : ''}
    </div>`;
}

function printOrders(orders: any[], storeName: string, title = 'Orders') {
  const body = orders.map(o => buildOrderHtml(o, storeName)).join('');
  const html = `<!DOCTYPE html><html><head><title>${title}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 36px 48px; color: #1e293b; }
      h2 { margin: 0 0 6px; font-size: 1.3rem; }
      .sub { color: #64748b; font-size: 0.82rem; margin-bottom: 28px; }
      @media print { body { margin: 20px; } }
    </style>
  </head><body>
    <h2>${storeName}</h2>
    <div class="sub">${title} — ${new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}</div>
    ${body}
  </body></html>`;

  // Use a hidden iframe to avoid popup blockers
  const existing = document.getElementById('__print_frame__');
  if (existing) existing.remove();
  const iframe = document.createElement('iframe');
  iframe.id = '__print_frame__';
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) return;
  doc.open(); doc.write(html); doc.close();
  iframe.contentWindow?.focus();
  setTimeout(() => {
    iframe.contentWindow?.print();
  }, 250);
}

function exportCSV(orders: any[]) {
  const header = ['Order #','Date','Customer','Email','Bake Date','Items','Subtotal','Discount','Tax','Tip','Total','Status','Delivery Address','Notes'];
  const rows = orders.map(o => [
    o.order_number,
    new Date(o.created_at).toLocaleDateString('en-US'),
    o.member_name || o.guest_name || '',
    o.guest_email || '',
    o.bake_date || '',
    (o.items || []).map((i: any) => `${i.quantity}x ${i.product_name}`).join('; '),
    parseFloat(o.subtotal || o.total_amount || 0).toFixed(2),
    parseFloat(o.discount_amount || 0).toFixed(2),
    parseFloat(o.tax_amount || 0).toFixed(2),
    parseFloat(o.tip_amount || 0).toFixed(2),
    parseFloat(o.total_amount || 0).toFixed(2),
    o.status,
    (o.delivery_address || '').replace(/,/g, ' '),
    (o.notes || '').replace(/,/g, ' '),
  ]);
  const csv = [header, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'store-orders.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function WebstoreOrders() {
  const [orders, setOrders] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Derive store name from local user data for print header
  const storeName = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').store_name || 'Bakery'; } catch { return 'Bakery'; } })();

  const load = useCallback(async () => {
    try {
      const res = await companyAPI.getRetailOrders();
      setOrders(Array.isArray(res.data) ? res.data : []);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (orderId: number, newStatus: string) => {
    setUpdatingStatus(true);
    try {
      const res = await companyAPI.updateRetailOrder(orderId, { status: newStatus });
      setOrders(prev => prev.map(o => o.id === orderId ? res.data : o));
      if (selected?.id === orderId) setSelected(res.data);
    } catch {}
    setUpdatingStatus(false);
  };

  const filtered = orders.filter(o => {
    const matchSearch = `${o.order_number} ${o.member_name} ${o.guest_email} ${o.guest_name}`.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || o.status === statusFilter;
    const orderDate = o.created_at ? o.created_at.slice(0, 10) : '';
    const matchFrom = !dateFrom || orderDate >= dateFrom;
    const matchTo   = !dateTo   || orderDate <= dateTo;
    return matchSearch && matchStatus && matchFrom && matchTo;
  });

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 style={{ fontWeight: 800, color: '#1e293b', marginBottom: '4px' }}>
              <ShoppingBagIcon style={{ width: 22, height: 22, marginRight: 8, color: '#059669', verticalAlign: 'middle' }} />
              Store Orders
            </h4>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>
              {filtered.length} of {orders.length} orders
            </p>
          </div>
          {/* Export / Print all */}
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-secondary" style={{ borderRadius: '8px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 5 }}
              onClick={() => exportCSV(filtered)} disabled={filtered.length === 0}>
              <ArrowDownTrayIcon style={{ width: 14, height: 14 }} /> Export CSV
            </Button>
            <Button size="sm" variant="outline-secondary" style={{ borderRadius: '8px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 5 }}
              onClick={() => printOrders(filtered, storeName, `${filtered.length} Orders`)} disabled={filtered.length === 0}>
              <PrinterIcon style={{ width: 14, height: 14 }} /> Print All
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
          <div style={{ position: 'relative', minWidth: 200, flex: 1 }}>
            <MagnifyingGlassIcon style={{ width: 15, height: 15, position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <Form.Control size="sm" placeholder="Search by order #, name, email…" value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: '30px' }} />
          </div>
          <Form.Select size="sm" style={{ width: 155 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            {ORDER_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </Form.Select>
          <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.82rem', color: '#64748b' }}>
            <span>From</span>
            <Form.Control size="sm" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ width: 148 }} />
          </div>
          <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.82rem', color: '#64748b' }}>
            <span>To</span>
            <Form.Control size="sm" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ width: 148 }} />
          </div>
          {(dateFrom || dateTo || statusFilter !== 'all' || search) && (
            <Button size="sm" variant="link" style={{ color: '#94a3b8', fontSize: '0.8rem', padding: 0 }}
              onClick={() => { setSearch(''); setStatusFilter('all'); setDateFrom(''); setDateTo(''); }}>
              Clear filters
            </Button>
          )}
        </div>

        <Card className="border-0 shadow-sm" style={{ borderRadius: '14px' }}>
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-success" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-5" style={{ color: '#94a3b8' }}>
              <ShoppingBagIcon style={{ width: 40, height: 40, marginBottom: 12, color: '#e2e8f0' }} />
              <p style={{ fontWeight: 500 }}>{search || dateFrom || dateTo ? 'No matching orders' : 'No store orders yet'}</p>
              <p style={{ fontSize: '0.82rem' }}>Orders placed through your /store page will appear here.</p>
            </div>
          ) : (
            <Table className="mb-0" style={{ fontSize: '0.88rem' }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: '#475569' }}>Order #</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: '#475569' }}>Customer</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: '#475569' }}>Date</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: '#475569' }}>Items</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: '#475569' }}>Total</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: '#475569' }}>Bake Date</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: '#475569' }}>Status</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: '#475569' }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(o => {
                  const sc = STATUS_COLORS[o.status] || STATUS_COLORS.pending;
                  return (
                    <tr key={o.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px 20px', fontWeight: 700, color: '#059669' }}>{o.order_number}</td>
                      <td style={{ padding: '12px 20px', color: '#374151' }}>{o.member_name || o.guest_name || '—'}</td>
                      <td style={{ padding: '12px 20px', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {o.created_at ? new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                      <td style={{ padding: '12px 20px', color: '#64748b' }}>{o.items?.length ?? 0} item{o.items?.length !== 1 ? 's' : ''}</td>
                      <td style={{ padding: '12px 20px', fontWeight: 600, color: '#1e293b' }}>${parseFloat(o.total_amount).toFixed(2)}</td>
                      <td style={{ padding: '12px 20px', color: '#64748b' }}>{o.bake_date || '—'}</td>
                      <td style={{ padding: '12px 20px' }}>
                        <Badge bg="" style={{ ...sc, fontSize: '0.75rem', borderRadius: '12px' }}>
                          {o.status.charAt(0).toUpperCase() + o.status.slice(1)}
                        </Badge>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div className="d-flex gap-2">
                          <Button variant="link" size="sm" className="p-0" style={{ color: '#059669', fontSize: '0.8rem' }} onClick={() => setSelected(o)}>
                            View
                          </Button>
                          <Button variant="link" size="sm" className="p-0" style={{ color: '#64748b', fontSize: '0.8rem' }} onClick={() => printOrders([o], storeName, `Order ${o.order_number}`)}>
                            Print
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card>

        {/* Order detail modal */}
        <Modal show={!!selected} onHide={() => setSelected(null)} size="lg" centered>
          {selected && (() => {
            const sc = STATUS_COLORS[selected.status] || STATUS_COLORS.pending;
            const disc = parseFloat(selected.discount_amount || 0);
            const tax  = parseFloat(selected.tax_amount || 0);
            const tip  = parseFloat(selected.tip_amount || 0);
            return (
              <>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <div className="d-flex align-items-center gap-3">
                    <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 700 }}>
                      Order {selected.order_number}
                    </Modal.Title>
                    <div className="d-flex gap-2">
                      <Button size="sm" variant="outline-secondary" style={{ borderRadius: '8px', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4 }}
                        onClick={() => printOrders([selected], storeName, `Order ${selected.order_number}`)}>
                        <PrinterIcon style={{ width: 13, height: 13 }} /> Print / PDF
                      </Button>
                    </div>
                  </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                  <Row className="g-3 mb-3">
                    <Col xs={6}>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Customer</div>
                      <div style={{ fontWeight: 600 }}>{selected.member_name || selected.guest_name || 'Guest'}</div>
                      {selected.guest_email && <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{selected.guest_email}</div>}
                      {selected.guest_phone && <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{selected.guest_phone}</div>}
                    </Col>
                    <Col xs={6}>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Bake Date</div>
                      <div style={{ fontWeight: 600 }}>{selected.bake_date || '—'}</div>
                    </Col>
                    <Col xs={6}>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Ordered</div>
                      <div style={{ fontWeight: 600 }}>{selected.created_at ? new Date(selected.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'}</div>
                    </Col>
                    <Col xs={6}>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Payment</div>
                      <div style={{ fontWeight: 600 }}>{selected.payment_status}</div>
                    </Col>
                    <Col xs={6}>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Fulfillment</div>
                      {selected.delivery_address ? (
                        <div style={{ fontWeight: 600 }}>🚚 Delivery</div>
                      ) : (
                        <div style={{ fontWeight: 600 }}>🛍 Pickup</div>
                      )}
                    </Col>
                    {selected.delivery_address && (
                      <Col xs={12}>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Delivery Address</div>
                        <div style={{ fontWeight: 500 }}>{selected.delivery_address}</div>
                      </Col>
                    )}
                  </Row>

                  <Table size="sm" className="mb-3">
                    <thead style={{ background: '#f8fafc' }}>
                      <tr>
                        <th>Item</th>
                        <th>Qty</th>
                        <th>Price</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.items?.map((item: any, i: number) => (
                        <tr key={i}>
                          <td>{item.product_name}</td>
                          <td>{item.quantity}</td>
                          <td>${parseFloat(item.product_price).toFixed(2)}</td>
                          <td className="text-end">${parseFloat(item.line_total).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot style={{ borderTop: '2px solid #e2e8f0' }}>
                      {disc > 0 && <tr><td colSpan={3} style={{ color: '#059669', fontSize: '0.82rem' }}>Discount</td><td className="text-end" style={{ color: '#059669' }}>−${disc.toFixed(2)}</td></tr>}
                      {tax  > 0 && <tr><td colSpan={3} style={{ fontSize: '0.82rem', color: '#64748b' }}>Tax</td><td className="text-end" style={{ color: '#64748b' }}>${tax.toFixed(2)}</td></tr>}
                      {tip  > 0 && <tr><td colSpan={3} style={{ fontSize: '0.82rem', color: '#64748b' }}>Tip</td><td className="text-end" style={{ color: '#64748b' }}>${tip.toFixed(2)}</td></tr>}
                      <tr style={{ fontWeight: 800 }}><td colSpan={3}>Total</td><td className="text-end" style={{ color: '#059669' }}>${parseFloat(selected.total_amount).toFixed(2)}</td></tr>
                    </tfoot>
                  </Table>

                  {selected.notes && <p style={{ fontSize: '0.85rem', color: '#64748b' }}><strong>Notes:</strong> {selected.notes}</p>}

                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '6px' }}>Update Status</div>
                    <div className="d-flex gap-2 flex-wrap">
                      {ORDER_STATUSES.map(s => {
                        const c = STATUS_COLORS[s];
                        return (
                          <button key={s} disabled={updatingStatus || selected.status === s} onClick={() => updateStatus(selected.id, s)}
                            style={{ padding: '5px 14px', borderRadius: '20px', border: `2px solid ${selected.status === s ? c.color : '#e2e8f0'}`, background: selected.status === s ? c.bg : 'white', color: selected.status === s ? c.color : '#374151', fontWeight: selected.status === s ? 700 : 400, fontSize: '0.8rem', cursor: 'pointer' }}>
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </Modal.Body>
              </>
            );
          })()}
        </Modal>
      </Container>
    </CompanyLayout>
  );
}
